import type { Hex } from 'viem'

export type PersistedAuthMode = 'passkey' | 'wallet' | 'atproto' | null

export interface StoredPasskeyCredential {
  id: string
  publicKey: Hex
  rpId: string
  username: string
  createdAt: string
}

interface PersistedAuthSession {
  mode: Exclude<PersistedAuthMode, null>
  address?: string
  playerId: string | null
  timestamp: string
}

const AUTH_MODE_KEY = 'wefa_auth_mode'
const AUTH_SESSION_KEY = 'wefa_auth_session_v2'
const PASSKEY_CREDENTIAL_KEY = 'wefa_passkey_credential'

function isBrowser(): boolean {
  return typeof window !== 'undefined'
}

export function getAuthMode(): PersistedAuthMode {
  if (!isBrowser()) return null
  const mode = window.localStorage.getItem(AUTH_MODE_KEY)
  if (mode === 'passkey' || mode === 'wallet' || mode === 'atproto') return mode
  return null
}

export function setAuthMode(mode: Exclude<PersistedAuthMode, null>): void {
  if (!isBrowser()) return
  window.localStorage.setItem(AUTH_MODE_KEY, mode)
}

export function clearAuthMode(): void {
  if (!isBrowser()) return
  window.localStorage.removeItem(AUTH_MODE_KEY)
}

export function getStoredAuthSession(): PersistedAuthSession | null {
  if (!isBrowser()) return null
  const raw = window.localStorage.getItem(AUTH_SESSION_KEY)
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as PersistedAuthSession
    if (
      (parsed.mode !== 'passkey' && parsed.mode !== 'wallet' && parsed.mode !== 'atproto') ||
      (parsed.address !== undefined && typeof parsed.address !== 'string')
    ) {
      return null
    }
    return parsed
  } catch {
    return null
  }
}

export function setStoredAuthSession(session: PersistedAuthSession): void {
  if (!isBrowser()) return
  window.localStorage.setItem(AUTH_SESSION_KEY, JSON.stringify(session))
}

export function clearStoredAuthSession(): void {
  if (!isBrowser()) return
  window.localStorage.removeItem(AUTH_SESSION_KEY)
}

export function getStoredCredential(): StoredPasskeyCredential | null {
  if (!isBrowser()) return null
  const raw = window.localStorage.getItem(PASSKEY_CREDENTIAL_KEY)
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as StoredPasskeyCredential
    if (
      typeof parsed.id !== 'string' ||
      typeof parsed.publicKey !== 'string' ||
      typeof parsed.rpId !== 'string' ||
      typeof parsed.username !== 'string'
    ) {
      return null
    }
    return parsed
  } catch {
    return null
  }
}

export function setStoredCredential(credential: StoredPasskeyCredential): void {
  if (!isBrowser()) return
  window.localStorage.setItem(PASSKEY_CREDENTIAL_KEY, JSON.stringify(credential))
}

export function hasStoredCredential(): boolean {
  return getStoredCredential() !== null
}

export function clearStoredCredential(): void {
  if (!isBrowser()) return
  window.localStorage.removeItem(PASSKEY_CREDENTIAL_KEY)
}

export function clearAllAuthStorage(): void {
  clearAuthMode()
  clearStoredAuthSession()
  clearStoredCredential()
}
